/**
 * AD-040 (2026-06-24) — STRUCTURED Design Programme (A→H).
 *
 * Replaces the single "What spaces do you need?" textarea scaffold with a real,
 * structured requirements brief — the shared design brain for L2C · L2A · L3A ·
 * L3B. UI contract: AVBP_DESIGN_PROGRAMME_INTAKE_MOCKUP_2026-06-24.html.
 *
 * Sections (H tier-depth deferred — full depth rendered now):
 *   A · Space schedule        — table: space · qty · target area · floor · priority
 *   B · Lifestyle             — household type · WFH · hosting · future-proofing
 *   C · Adjacencies           — top-3 priority spaces + near/away rules
 *   D · Special functions     — amenity checklist + qty
 *   E · Style                 — typology · leaning · materials · references
 *   F · Budget & scope        — scope · budget · phasing · timeline
 *   G · Constraints           — must-keeps checklist + free text
 *
 * Firestore shape: stored as ONE structured map at `answers.designProgramme`
 * (a REAL module owns it). Sibling to `answers.drawings`. The pure builder
 * (`programmeToAnswer`) + loader (`programmeFromAnswer`) keep the contract clean;
 * empty optional fields are dropped so a merge:true save never writes noise.
 *
 * Firebase-free + React-free so the `.cjs` runner exercises the shape mapping.
 */

/* ───────────────────────────── option sets ───────────────────────────── */

export const SPACE_PRIORITIES = ['Must-have', 'Nice-to-have'] as const;
export type SpacePriority = (typeof SPACE_PRIORITIES)[number];

export const HOUSEHOLD_TYPES = ['Nuclear', 'Joint family', 'Single', 'Other'] as const;
export const WFH_OPTIONS = ['No', 'Yes — 1 person', 'Yes — 2+'] as const;
export const HOSTING_OPTIONS = ['Rare', 'Occasional', 'Frequent / large'] as const;

export const FUTURE_PROOFING = [
  'Growing family (more kids)',
  'Ageing parents / accessibility',
  'Rental / income unit',
  'Home-based business',
  'Pets',
  'Live-in staff',
] as const;

export const SPECIAL_FUNCTIONS = [
  'Pooja / prayer room',
  'Home office / study',
  'Car parking',
  'Staff / servant quarters',
  'Dedicated storage / utility',
  'Lift / elevator',
  'Home theatre',
  'Gym / yoga',
  'Garden / landscape',
  'Swimming pool',
  'Terrace / party deck',
  'Bore well / rainwater',
  'Solar / backup power',
  'Shop / commercial unit',
  'Guest suite',
] as const;

export const ARCH_STYLES = [
  'Contemporary',
  'Modern minimal',
  'Traditional Indian',
  'Indo-contemporary',
  'Colonial',
  'Other',
] as const;
export const INTERIOR_LEANINGS = [
  'Warm / earthy',
  'Light / neutral',
  'Rich / classic',
  'Bold / colourful',
] as const;
export const MATERIAL_PREFS = [
  'Natural stone',
  'Exposed brick',
  'Wood',
  'Concrete',
  'Large windows / light',
  'Jaali / screens',
  'Green walls',
] as const;

export const SCOPE_OF_WORK = [
  'New build',
  'Renovation / remodel',
  'Interior only',
  'Extension / add floor',
] as const;
export const BUDGET_RANGES = [
  'Under ₹25 L',
  '₹25–50 L',
  '₹50 L – 1 Cr',
  '₹1–2 Cr',
  '₹2 Cr +',
  'Prefer not to say',
] as const;
export const PHASING_OPTIONS = ['Single phase', 'Phased over time', 'Not decided'] as const;
export const START_OPTIONS = ['Immediate', 'Within 3 months', '3–6 months', '6 months +'] as const;

export const CONSTRAINT_CHECKS = [
  'Existing structure to retain',
  'Trees to preserve',
  'Slope / level difference',
  'Shared / common wall',
  'Existing borewell / septic',
  'Overhead lines / easement',
] as const;

export const ADJACENCY_KINDS = ['near', 'away'] as const;
export type AdjacencyKind = (typeof ADJACENCY_KINDS)[number];

/* H · COORDINATION · project team (BATCH 1, 2026-06-25) — moved out of the
 * drawings block; a repeatable, role-tagged list that links existing contacts
 * (or adds new, dup-detected) on the contact spine. Canonical open-ended roles;
 * "Other…" names a custom role saved to the shared reusable role list. */
export const COORDINATION_ROLES = [
  'Architect',
  'Interior designer',
  'Contractor',
  'HVAC consultant',
  'Structural consultant',
  'MEP consultant',
  'Landscape consultant',
  'Project / site manager (client side)',
  'Operations / facility manager',
] as const;
export type CoordinationRole = (typeof COORDINATION_ROLES)[number];

/** The role-tag key stored on a Contact's `roleTags[]` for a coordination member. */
export function roleToTag(role: string): string {
  return (role || 'other').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'other';
}

/* ─────────────────────────────── shapes ──────────────────────────────── */

/** A · one row in the space schedule. */
export interface SpaceRow {
  id: string;
  space: string;
  qty: string;
  targetArea: string; // sq ft, string in the form
  floor: string;
  priority: SpacePriority | '';
}

/** C · one adjacency rule (near/away between two spaces). */
export interface AdjacencyRule {
  id: string;
  kind: AdjacencyKind;
  a: string;
  b: string;
}

/** D · a special function selection (checked + optional qty). */
export interface SpecialFunctionSel {
  name: string;
  qty?: string;
}

/** H · one coordination team member (links a Contact, or holds a new one). */
export interface CoordinationMember {
  /** Stable form-local row id. */
  id: string;
  /** Role (a COORDINATION_ROLES value OR a custom role string). */
  role: string;
  /** Linked Contact (clients/{cid}) when an existing contact is chosen. */
  contactId?: string;
  /** Display name (cache for a linked contact / the new-contact name). */
  name: string;
  email?: string;
  whatsapp?: string;
}

/** The whole structured design programme (form-local state). */
export interface DesignProgrammeInput {
  /* A */ spaces: SpaceRow[];
  /* B */ householdType: string;
  wfh: string;
  hosting: string;
  futureProofing: string[];
  /* C */ prioritySpaces: string[]; // up to 3, ranked
  adjacencies: AdjacencyRule[];
  /* D */ specialFunctions: SpecialFunctionSel[];
  specialFunctionsNote: string;
  /* E */ archStyle: string;
  interiorLeaning: string;
  materials: string[];
  references: string;
  /* F */ scopeOfWork: string;
  budgetRange: string;
  phasing: string;
  desiredStart: string;
  /* G */ constraints: string[];
  mustKeeps: string;
  /* H */ coordination: CoordinationMember[];
}

/* ── Per-tier DESIGN-BRIEF SECTION FLAGS (the deferred "H per-tier depth"
 * mechanism, BATCH 2). Each section can be required / optional / on / off per
 * tier. `off` hides the section; `required` marks it must-fill; `optional`/`on`
 * both show it (on = shown by default, optional = explicitly optional copy). The
 * REQUIRED flag drives a (soft for staff) completeness check + a required marker.
 */
export type SectionFlag = 'required' | 'optional' | 'on' | 'off';

export type DesignBriefSection =
  | 'spaceSchedule'  // A
  | 'lifestyle'      // B
  | 'adjacencies'    // C
  | 'specialFunctions' // D
  | 'style'          // E
  | 'budget'         // F
  | 'constraints'    // G
  | 'coordination';  // H

export type DesignBriefSectionFlags = Record<DesignBriefSection, SectionFlag>;

/** Default = everything ON (full depth) — the pre-flag behaviour. */
export const DEFAULT_SECTION_FLAGS: DesignBriefSectionFlags = {
  spaceSchedule: 'on',
  lifestyle: 'on',
  adjacencies: 'on',
  specialFunctions: 'on',
  style: 'on',
  budget: 'on',
  constraints: 'on',
  coordination: 'on',
};

/**
 * Per-tier overrides (sparse — only sections that differ from DEFAULT). L2A is
 * configured per the owner spec 2026-06-25; the others inherit DEFAULT until the
 * owner defines them. Keyed by the SERVICE key (L2A/L2C…), `_R{n}` stripped by
 * `designBriefFlagsForTier`. Coordination is a CONFIRM-PENDING owner flag on L2A
 * (the owner listed A–G; H assumed OFF — flagged for review).
 */
export const TIER_SECTION_FLAGS: Record<string, Partial<DesignBriefSectionFlags>> = {
  // L2A (owner spec 2026-06-25): A req · B opt · C/D on · E/F off · G on · H off.
  L2A: {
    spaceSchedule: 'required',
    lifestyle: 'optional',
    adjacencies: 'on',
    specialFunctions: 'on',
    style: 'off',
    budget: 'off',
    constraints: 'on',
    coordination: 'off', // ASSUMED OFF (owner listed A–G only) — confirm.
  },
  // L2C (owner spec AD-042): ALL A–H ON; A required as the spine; H Coordination ON.
  L2C: {
    spaceSchedule: 'required',
    lifestyle: 'on',
    adjacencies: 'on',
    specialFunctions: 'on',
    style: 'on',
    budget: 'on',
    constraints: 'on',
    coordination: 'on',
  },
  // L2D · Vistara (detailing) — detailing an existing plan: A off · B off · C off ·
  // D on · E optional · F optional · G on · H on. (E·Style optional flagged.)
  L2D: {
    spaceSchedule: 'off',
    lifestyle: 'off',
    adjacencies: 'off',
    specialFunctions: 'on',
    style: 'optional',
    budget: 'optional',
    constraints: 'on',
    coordination: 'on',
  },
  // L3A · Sankalpa (brief) — FULL depth, all A–H ON; A required as the spine.
  L3A: {
    spaceSchedule: 'required',
    lifestyle: 'on',
    adjacencies: 'on',
    specialFunctions: 'on',
    style: 'on',
    budget: 'on',
    constraints: 'on',
    coordination: 'on',
  },
  // L3B · Sthapana (design) — FULL depth, all A–H ON; A required as the spine.
  L3B: {
    spaceSchedule: 'required',
    lifestyle: 'on',
    adjacencies: 'on',
    specialFunctions: 'on',
    style: 'on',
    budget: 'on',
    constraints: 'on',
    coordination: 'on',
  },
};

/** PURE — resolve a tier (e.g. `L2A_R1`, `L2A`) → its design-brief section flags. */
export function designBriefFlagsForTier(tier: string): DesignBriefSectionFlags {
  const key = (tier || '').replace(/_R\d+$/i, '').toUpperCase();
  const over = TIER_SECTION_FLAGS[key] || {};
  return { ...DEFAULT_SECTION_FLAGS, ...over };
}

/** PURE — is a section shown at all for these flags? */
export function sectionShown(flag: SectionFlag): boolean {
  return flag !== 'off';
}
/** PURE — is a section required for these flags? */
export function sectionRequired(flag: SectionFlag): boolean {
  return flag === 'required';
}

/* ──────────────────────────── pure helpers ───────────────────────────── */

function rid(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}`;
}

/** A blank space row. */
export function blankSpaceRow(): SpaceRow {
  return { id: rid('sp'), space: '', qty: '1', targetArea: '', floor: '', priority: '' };
}

/** A blank adjacency rule. */
export function blankAdjacency(): AdjacencyRule {
  return { id: rid('adj'), kind: 'near', a: '', b: '' };
}

/** A blank coordination member (CHOOSE state — no contact, no role yet). */
export function blankCoordinationMember(role = 'Architect'): CoordinationMember {
  return { id: rid('co'), role, name: '' };
}

/** A blank programme — empty everything (one starter space row). */
export function blankDesignProgramme(): DesignProgrammeInput {
  return {
    spaces: [],
    householdType: '',
    wfh: '',
    hosting: '',
    futureProofing: [],
    prioritySpaces: [],
    adjacencies: [],
    specialFunctions: [],
    specialFunctionsNote: '',
    archStyle: '',
    interiorLeaning: '',
    materials: [],
    references: '',
    scopeOfWork: '',
    budgetRange: '',
    phasing: '',
    desiredStart: '',
    constraints: [],
    mustKeeps: '',
    coordination: [],
  };
}

/** PURE — running total of target areas across the schedule. */
export function spaceScheduleTotal(spaces: SpaceRow[]): number {
  let total = 0;
  for (const s of spaces) {
    const area = Number((s.targetArea || '').trim());
    const qty = Number((s.qty || '1').trim()) || 1;
    if (Number.isFinite(area) && area > 0) total += area * qty;
  }
  return total;
}

/** PURE — does the programme carry ANY content worth persisting? */
export function programmeHasContent(p: DesignProgrammeInput): boolean {
  return Boolean(
    p.spaces.some((s) => (s.space || '').trim() || (s.targetArea || '').trim()) ||
      p.householdType ||
      p.wfh ||
      p.hosting ||
      p.futureProofing.length ||
      p.prioritySpaces.length ||
      p.adjacencies.some((a) => a.a || a.b) ||
      p.specialFunctions.length ||
      (p.specialFunctionsNote || '').trim() ||
      p.archStyle ||
      p.interiorLeaning ||
      p.materials.length ||
      (p.references || '').trim() ||
      p.scopeOfWork ||
      p.budgetRange ||
      p.phasing ||
      p.desiredStart ||
      p.constraints.length ||
      (p.mustKeeps || '').trim() ||
      (p.coordination || []).some((m) => m.contactId || (m.name || '').trim() || m.email || m.whatsapp),
  );
}

/** PURE — coordination members that carry a linked contact or any captured detail. */
export function meaningfulCoordination(p: DesignProgrammeInput): CoordinationMember[] {
  return (p.coordination || []).filter(
    (m) => m.contactId || (m.name || '').trim() || m.email || m.whatsapp,
  );
}

function t(v: string | undefined): string {
  return (v || '').trim();
}

/**
 * PURE — structured programme → `answers.designProgramme` map. Empty optional
 * fields dropped; an empty programme returns {} (caller omits the key entirely).
 */
export function programmeToAnswer(p: DesignProgrammeInput): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  // A · space schedule
  const spaces = p.spaces
    .filter((s) => t(s.space) || t(s.targetArea))
    .map((s) => {
      const row: Record<string, unknown> = { id: s.id, space: t(s.space) };
      if (t(s.qty)) row.qty = t(s.qty);
      const area = Number(t(s.targetArea));
      if (Number.isFinite(area) && area > 0) row.targetArea = area;
      if (t(s.floor)) row.floor = t(s.floor);
      if (s.priority) row.priority = s.priority;
      return row;
    });
  if (spaces.length) out.spaces = spaces;
  // B · lifestyle
  if (t(p.householdType)) out.householdType = t(p.householdType);
  if (t(p.wfh)) out.wfh = t(p.wfh);
  if (t(p.hosting)) out.hosting = t(p.hosting);
  if (p.futureProofing.length) out.futureProofing = p.futureProofing;
  // C · adjacencies
  if (p.prioritySpaces.length) out.prioritySpaces = p.prioritySpaces;
  const adj = p.adjacencies
    .filter((a) => t(a.a) && t(a.b))
    .map((a) => ({ id: a.id, kind: a.kind, a: t(a.a), b: t(a.b) }));
  if (adj.length) out.adjacencies = adj;
  // D · special functions
  const fns = p.specialFunctions
    .filter((f) => t(f.name))
    .map((f) => (t(f.qty) ? { name: t(f.name), qty: t(f.qty) } : { name: t(f.name) }));
  if (fns.length) out.specialFunctions = fns;
  if (t(p.specialFunctionsNote)) out.specialFunctionsNote = t(p.specialFunctionsNote);
  // E · style
  if (t(p.archStyle)) out.archStyle = t(p.archStyle);
  if (t(p.interiorLeaning)) out.interiorLeaning = t(p.interiorLeaning);
  if (p.materials.length) out.materials = p.materials;
  if (t(p.references)) out.references = t(p.references);
  // F · budget & scope
  if (t(p.scopeOfWork)) out.scopeOfWork = t(p.scopeOfWork);
  if (t(p.budgetRange)) out.budgetRange = t(p.budgetRange);
  if (t(p.phasing)) out.phasing = t(p.phasing);
  if (t(p.desiredStart)) out.desiredStart = t(p.desiredStart);
  // G · constraints
  if (p.constraints.length) out.constraints = p.constraints;
  if (t(p.mustKeeps)) out.mustKeeps = t(p.mustKeeps);
  // H · coordination · project team
  const coord = (p.coordination || [])
    .filter((m) => m.contactId || t(m.name) || m.email || m.whatsapp)
    .map((m) => {
      const row: Record<string, unknown> = { id: m.id, role: t(m.role) || 'Other' };
      if (m.contactId) row.contactId = m.contactId;
      if (t(m.name)) row.name = t(m.name);
      if (t(m.email)) row.email = t(m.email);
      if (t(m.whatsapp)) row.whatsapp = t(m.whatsapp);
      return row;
    });
  if (coord.length) out.coordination = coord;
  return out;
}

function asStr(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}
function asStrArr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

/** PURE — `answers.designProgramme` map → structured form input (defensive). */
export function programmeFromAnswer(raw: unknown): DesignProgrammeInput {
  const d = (raw || {}) as Record<string, unknown>;
  const blank = blankDesignProgramme();
  const spacesRaw = Array.isArray(d.spaces) ? d.spaces : [];
  const adjRaw = Array.isArray(d.adjacencies) ? d.adjacencies : [];
  const fnsRaw = Array.isArray(d.specialFunctions) ? d.specialFunctions : [];
  return {
    ...blank,
    spaces: spacesRaw.map((r) => {
      const o = (r || {}) as Record<string, unknown>;
      const prio = asStr(o.priority);
      return {
        id: asStr(o.id) || rid('sp'),
        space: asStr(o.space),
        qty: o.qty != null ? asStr(o.qty) : '1',
        targetArea: o.targetArea != null ? asStr(o.targetArea) : '',
        floor: asStr(o.floor),
        priority: (SPACE_PRIORITIES as readonly string[]).includes(prio) ? (prio as SpacePriority) : '',
      };
    }),
    householdType: asStr(d.householdType),
    wfh: asStr(d.wfh),
    hosting: asStr(d.hosting),
    futureProofing: asStrArr(d.futureProofing),
    prioritySpaces: asStrArr(d.prioritySpaces),
    adjacencies: adjRaw.map((r) => {
      const o = (r || {}) as Record<string, unknown>;
      const kind = asStr(o.kind);
      return {
        id: asStr(o.id) || rid('adj'),
        kind: (ADJACENCY_KINDS as readonly string[]).includes(kind) ? (kind as AdjacencyKind) : 'near',
        a: asStr(o.a),
        b: asStr(o.b),
      };
    }),
    specialFunctions: fnsRaw.map((r) => {
      const o = (r || {}) as Record<string, unknown>;
      return o.qty != null ? { name: asStr(o.name), qty: asStr(o.qty) } : { name: asStr(o.name) };
    }),
    specialFunctionsNote: asStr(d.specialFunctionsNote),
    archStyle: asStr(d.archStyle),
    interiorLeaning: asStr(d.interiorLeaning),
    materials: asStrArr(d.materials),
    references: asStr(d.references),
    scopeOfWork: asStr(d.scopeOfWork),
    budgetRange: asStr(d.budgetRange),
    phasing: asStr(d.phasing),
    desiredStart: asStr(d.desiredStart),
    constraints: asStrArr(d.constraints),
    mustKeeps: asStr(d.mustKeeps),
    coordination: (Array.isArray(d.coordination) ? d.coordination : []).map((r) => {
      const o = (r || {}) as Record<string, unknown>;
      return {
        id: asStr(o.id) || rid('co'),
        role: asStr(o.role) || 'Other',
        ...(asStr(o.contactId) ? { contactId: asStr(o.contactId) } : {}),
        name: asStr(o.name),
        ...(asStr(o.email) ? { email: asStr(o.email) } : {}),
        ...(asStr(o.whatsapp) ? { whatsapp: asStr(o.whatsapp) } : {}),
      };
    }),
  };
}
